import { Card, Col, Row, Statistic, Alert, Tag, Empty } from "antd";
import React, { useEffect, useState } from "react";
import { API_METHODS, BLANK_VALUE } from "../constants";
import useApi from "../hooks/useApi";
import TableComponent from "./common/TableComponent";
import {
  CalendarOutlined,
  DollarOutlined,
  UserOutlined,
  ShoppingOutlined,
  ClockCircleOutlined,
  TrophyOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  TeamOutlined,
  CreditCardOutlined,
} from "@ant-design/icons";

function Dashboard() {
  // API calls following your pattern
  const [todayOps, getTodayOps] = useApi(
    "/admin/dashboard/today",
    null,
    API_METHODS.GET
  );
  const [tomorrowOps, getTomorrowOps] = useApi(
    "/admin/dashboard/tommorow",
    null,
    API_METHODS.GET
  );
  const [weekOps, getWeekOps] = useApi(
    "/admin/dashboard/week",
    null,
    API_METHODS.GET
  );
  const [revenue, getRevenue] = useApi(
    "/admin/dashboard/revenue",
    null,
    API_METHODS.GET
  );
  const [quickStats, getQuickStats] = useApi(
    "/admin/dashboard/quick-stat",
    null,
    API_METHODS.GET
  );
  const [recentActivity, getRecentActivity] = useApi(
    "/admin/dashboard/recent-activity",
    null,
    API_METHODS.GET
  );
  const [alerts, getAlerts] = useApi(
    "/admin/dashboard/alert",
    null,
    API_METHODS.GET
  );

  // Load all data on component mount
  useEffect(() => {
    getTodayOps();
    getTomorrowOps();
    getWeekOps();
    getRevenue();
    getQuickStats();
    getRecentActivity();
    getAlerts();
  }, []);

  // Format currency helper
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  // Format user name helper
  const formatUserName = (firstName, lastName, customerName, phone) => {
    return (
      customerName ||
      `${firstName || ""} ${lastName || ""}`.trim() ||
      phone ||
      "N/A"
    );
  };

  // Recent activity table columns
  const recentActivityColumns = [
    {
      title: "Order ID",
      dataIndex: "order_number",
      key: "order_number",
      render: (orderNumber) => <Tag color="blue">{orderNumber}</Tag>,
    },
    {
      title: "Devotee Name",
      key: "user_name",
      render: (record) => formatUserName(record.first_name, record.last_name, record.customer_name),
    },
    {
      title: "Amount",
      dataIndex: "total_amount",
      key: "total_amount",
      render: (amount) => formatCurrency(amount),
    },
    {
      title: "Status",
      dataIndex: "order_status",
      key: "order_status",
      render: (status) => {
        const colorMap = {
          confirmed: "green",
          pending: "orange",
          completed: "blue",
          cancelled: "red",
        };
        return (
          <Tag color={colorMap[status] || "default"}>
            {status?.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: "Created On",
      dataIndex: "created_on",
      key: "created_on",
      render: (date) => new Date(date).toLocaleDateString("en-IN"),
    },
  ];

  // Generate alerts based on data
  const generateAlerts = () => {
    const alertsList = [];

    // Check for failed payments
    if (alerts.data?.failed_payments > 0) {
      alertsList.push({
        message: "Failed Payments Alert",
        description: `${alerts.data.failed_payments} payments failed in the last 7 days. Please review and take action.`,
        type: "warning",
        showIcon: true,
      });
    }

    // Check for pending inquiries
    if (alerts.data?.pending_inquiries > 0) {
      alertsList.push({
        message: "Pending Support Inquiries",
        description: `${alerts.data.pending_inquiries} customer inquiries are waiting for response.`,
        type: "info",
        showIcon: true,
      });
    }

    // Check for pending service requests
    if (alerts.data?.pending_service_requests > 0) {
      alertsList.push({
        message: "New Service Requests",
        description: `${alerts.data.pending_service_requests} new service requests need attention.`,
        type: "info",
        showIcon: true,
      });
    }

    // Check if tomorrow has many bookings
    if (tomorrowOps.data?.total_bookings > 10) {
      alertsList.push({
        message: "High Booking Volume Tomorrow",
        description: `${tomorrowOps.data.total_bookings} bookings scheduled for tomorrow. Ensure adequate preparation.`,
        type: "success",
        showIcon: true,
      });
    }

    return alertsList;
  };

  return (
    <div style={{ padding: "24px" }}>
      <h1
        style={{ marginBottom: "24px", fontSize: "28px", fontWeight: "bold" }}
      >
        Mahalaxmi Puja Dashboard
      </h1>

      {/* Alerts Section */}
      {!alerts.isLoading && alerts.data && (
        <Row style={{ marginBottom: "24px" }}>
          <Col span={24}>
            {generateAlerts().map((alert, index) => (
              <Alert
                key={index}
                message={alert.message}
                description={alert.description}
                type={alert.type}
                showIcon={alert.showIcon}
                style={{ marginBottom: "8px" }}
                closable
              />
            ))}
          </Col>
        </Row>
      )}

      {/* Quick Stats Cards */}
      <Row gutter={16} style={{ marginBottom: "24px" }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Today's Schedule"
              value={todayOps.data?.total_bookings || 0}
              prefix={<CalendarOutlined />}
              loading={todayOps.isLoading}
              suffix="bookings"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Today's Revenue"
              value={revenue.data?.today_revenue || 0}
              prefix="₹"
              loading={revenue.isLoading}
              precision={0}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Active Users"
              value={quickStats.data?.active_users || 0}
              prefix={<UserOutlined />}
              loading={quickStats.isLoading}
              suffix="users"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Payment Success Rate"
              value={quickStats.data?.payment_success_rate || 0}
              prefix={<CreditCardOutlined />}
              loading={quickStats.isLoading}
              suffix="%"
              precision={1}
            />
          </Card>
        </Col>
      </Row>

      {/* Today & Tomorrow Operations */}
      <Row gutter={16} style={{ marginBottom: "24px" }}>
        <Col xs={24} md={12}>
          <Card
            title="Today's Operations"
            loading={todayOps.isLoading}
            extra={<CalendarOutlined />}
          >
            {todayOps.data ? (
              <div>
                <Row gutter={16} style={{ marginBottom: "16px" }}>
                  <Col span={8}>
                    <Statistic
                      title="Total"
                      value={todayOps.data.total_bookings}
                      prefix={<CalendarOutlined />}
                      valueStyle={{ fontSize: "18px" }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Confirmed"
                      value={todayOps.data.confirmed_orders}
                      prefix={<CheckCircleOutlined />}
                      valueStyle={{ fontSize: "18px", color: "#52c41a" }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Completed"
                      value={todayOps.data.completed_orders}
                      prefix={<TrophyOutlined />}
                      valueStyle={{ fontSize: "18px", color: "#1890ff" }}
                    />
                  </Col>
                </Row>

                {todayOps.data.pending_orders?.length > 0 ? (
                  <div>
                    <h4 style={{ marginBottom: "12px" }}>Pending Orders:</h4>
                    <div style={{ maxHeight: "150px", overflowY: "auto" }}>
                      {todayOps.data.pending_orders.slice(0, 5).map((order) => (
                        <div
                          key={order.id}
                          style={{
                            padding: "8px",
                            borderBottom: "1px solid #f0f0f0",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: "500" }}>
                              {formatUserName(
                                order.first_name,
                                order.last_name,
                                order.customer_name
                              )}
                            </div>
                            <div style={{ fontSize: "12px", color: "#666" }}>
                              {order.order_number}
                            </div>
                          </div>
                          <Tag color="orange">Pending</Tag>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Empty
                    description="No pending orders for today"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                )}
              </div>
            ) : (
              <Empty description="No data available" />
            )}
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card
            title="Tomorrow's Schedule"
            loading={tomorrowOps.isLoading}
            extra={<ClockCircleOutlined />}
          >
            {tomorrowOps.data ? (
              <div>
                <div style={{ marginBottom: "16px" }}>
                  <Statistic
                    title="Scheduled Bookings"
                    value={tomorrowOps.data.total_bookings}
                    prefix={<CalendarOutlined />}
                    suffix="bookings"
                  />
                  <p style={{ margin: "8px 0", color: "#666" }}>
                    Date:{" "}
                    {new Date(tomorrowOps.data.booking_date).toLocaleDateString(
                      "en-IN"
                    )}
                  </p>
                </div>

                {tomorrowOps.data.service_breakdown?.length > 0 && (
                  <div style={{ marginBottom: "16px" }}>
                    <h4>Service Breakdown:</h4>
                    <div style={{ maxHeight: "120px", overflowY: "auto" }}>
                      {tomorrowOps.data.service_breakdown.map(
                        (service, index) => (
                          <div
                            key={index}
                            style={{
                              padding: "6px 0",
                              display: "flex",
                              justifyContent: "space-between",
                              borderBottom: "1px solid #f5f5f5",
                            }}
                          >
                            <span style={{ fontSize: "14px" }}>
                              {service.name}
                            </span>
                            <Tag color="blue">
                              {service.booking_count} bookings
                            </Tag>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}

                {tomorrowOps.data.recent_orders?.length > 0 && (
                  <div>
                    <h4>Recent Orders:</h4>
                    <div style={{ maxHeight: "100px", overflowY: "auto" }}>
                      {tomorrowOps.data.recent_orders
                        .slice(0, 3)
                        .map((order) => (
                          <div
                            key={order.id}
                            style={{
                              padding: "6px 0",
                              fontSize: "12px",
                              color: "#666",
                            }}
                          >
                            {formatUserName(order.first_name, order.last_name, order.customer_name)}{" "}
                            - {order.order_number}
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Empty description="No bookings scheduled for tomorrow" />
            )}
          </Card>
        </Col>
      </Row>

      {/* Week Overview & Revenue */}
      <Row gutter={16} style={{ marginBottom: "24px" }}>
        <Col xs={24} md={12}>
          <Card title="This Week's Schedule" loading={weekOps.isLoading}>
            {weekOps.data ? (
              <div>
                <Statistic
                  title="Total Scheduled This Week"
                  value={weekOps.data.week_summary?.total_bookings || 0}
                  suffix="bookings"
                  style={{ marginBottom: "16px" }}
                />

                {weekOps.data.week_data?.length > 0 && (
                  <div>
                    <h4>Daily Breakdown:</h4>
                    <Row gutter={8}>
                      {weekOps.data.week_data.slice(0, 7).map((day) => (
                        <Col
                          span={3}
                          key={day.date}
                          style={{ textAlign: "center" }}
                        >
                          <div style={{ fontSize: "12px", color: "#666" }}>
                            {day.day_name}
                          </div>
                          <div style={{ fontSize: "16px", fontWeight: "bold" }}>
                            {day.booking_count}
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </div>
                )}
              </div>
            ) : (
              <Empty description="No week data available" />
            )}
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card title="Revenue Summary" loading={revenue.isLoading}>
            {revenue.data ? (
              <Row gutter={16}>
                <Col span={12}>
                  <Statistic
                    title="Today"
                    value={revenue.data.today_revenue}
                    prefix="₹"
                    precision={0}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Yesterday"
                    value={revenue.data.yesterday_revenue}
                    prefix="₹"
                    precision={0}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="This Week"
                    value={revenue.data.week_revenue}
                    prefix="₹"
                    precision={0}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="This Month"
                    value={revenue.data.month_revenue}
                    prefix="₹"
                    precision={0}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="All Time"
                    value={revenue.data.all_time_revenue}
                    prefix="₹"
                    precision={0}
                  />
                </Col>
              </Row>
            ) : (
              <Empty description="No revenue data available" />
            )}
          </Card>
        </Col>
      </Row>

      {/* Popular Service & User Stats */}
      <Row gutter={16} style={{ marginBottom: "24px" }}>
        <Col xs={24} md={12}>
          <Card
            title="Popular Service This Month"
            loading={quickStats.isLoading}
          >
            {quickStats.data?.popular_service ? (
              <div style={{ textAlign: "center", padding: "20px" }}>
                <TrophyOutlined
                  style={{
                    fontSize: "48px",
                    color: "#faad14",
                    marginBottom: "16px",
                  }}
                />
                <h3 style={{ margin: "8px 0" }}>
                  {quickStats.data.popular_service.name}
                </h3>
                <Tag color="gold" style={{ fontSize: "14px" }}>
                  {quickStats.data.popular_service.booking_count} bookings
                </Tag>
                <p style={{ margin: "8px 0", color: "#666" }}>
                  Type: {quickStats.data.popular_service.type}
                </p>
              </div>
            ) : (
              <Empty description="No popular service data" />
            )}
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card title="Quick Statistics" loading={quickStats.isLoading}>
            {quickStats.data ? (
              <Row gutter={16}>
                <Col span={12}>
                  <Statistic
                    title="Unique Visits"
                    value={quickStats.data.unqiue_visits}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Active Users"
                    value={quickStats.data.active_users}
                    prefix={<TeamOutlined />}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Payment Success Rate"
                    value={quickStats.data.payment_success_rate}
                    suffix="%"
                    precision={1}
                    prefix={<CreditCardOutlined />}
                    valueStyle={{
                      color:
                        quickStats.data.payment_success_rate > 90
                          ? "#52c41a"
                          : "#faad14",
                    }}
                  />
                </Col>
              </Row>
            ) : (
              <Empty description="No statistics available" />
            )}
          </Card>
        </Col>
      </Row>

      {/* Recent Activity Tables */}
      <Row gutter={16}>
        <Col xs={24} lg={12}>
          <Card title="Recent Orders" loading={recentActivity.isLoading}>
            {recentActivity.data?.latest_orders?.length > 0 ? (
              <TableComponent
                columns={recentActivityColumns}
                tableData={recentActivity.data.latest_orders}
                pagination={false}
                size="small"
              />
            ) : (
              <Empty description="No recent orders" />
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Recent Payments" loading={recentActivity.isLoading}>
            {recentActivity.data?.recent_payments?.length > 0 ? (
              <div>
                {recentActivity.data.recent_payments.map((payment, index) => (
                  <div
                    key={index}
                    style={{
                      padding: "12px",
                      borderBottom: "1px solid #f0f0f0",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: "500" }}>
                        {payment.order_number}
                      </div>
                      <div style={{ fontSize: "12px", color: "#666" }}>
                        {new Date(payment.created_on).toLocaleDateString(
                          "en-IN"
                        )}
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontWeight: "bold", color: "#52c41a" }}>
                        {formatCurrency(payment.amount)}
                      </div>
                      <Tag color="green" size="small">
                        {payment.payment_method || "Online"}
                      </Tag>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <Empty description="No recent payments" />
            )}
          </Card>
        </Col>
      </Row>

      {/* New Users */}
      {recentActivity.data?.new_users?.length > 0 && (
        <Row style={{ marginTop: "16px" }}>
          <Col span={24}>
            <Card
              title="New User Registrations"
              loading={recentActivity.isLoading}
            >
              <Row gutter={16}>
                {recentActivity.data.new_users
                  .slice(0, 5)
                  .map((user, index) => (
                    <Col xs={24} sm={12} md={8} lg={4} key={index}>
                      <Card size="small" style={{ marginBottom: "8px" }}>
                        <div style={{ textAlign: "center" }}>
                          <UserOutlined
                            style={{
                              fontSize: "24px",
                              color: "#1890ff",
                              marginBottom: "8px",
                            }}
                          />
                          <div style={{ fontWeight: "500", fontSize: "14px" }}>
                            {formatUserName(
                              user.first_name,
                              user.last_name,
                              null,
                              user.phone
                            )}
                          </div>
                          <div style={{ fontSize: "12px", color: "#666" }}>
                            {new Date(user.created_on).toLocaleDateString(
                              "en-IN"
                            )}
                          </div>
                        </div>
                      </Card>
                    </Col>
                  ))}
              </Row>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
}

export default Dashboard;
